/**
 * PluckinAroundGallery — curated photo gallery for the Social page.
 *
 * Scope (intentionally narrow):
 *   - Renders the "Pluckin' Around" section from `SOCIAL_GALLERY`
 *     (admin-curated, frontend-only).
 *   - CSS-only masonry via Tailwind `columns-*` — zero JS layout,
 *     zero dependencies. Each photo wraps in `break-inside-avoid`
 *     so it never splits across columns.
 *   - Native `loading="lazy"` on every photo.
 *   - Click any photo → opens a shadcn `Dialog`-based lightbox with
 *     prev / next / counter and keyboard arrow navigation. ESC and
 *     overlay-click close via Radix Dialog defaults.
 *   - When `SOCIAL_GALLERY.photos` is empty, renders the empty-state
 *     card with locked copy.
 *
 * Out of scope:
 *   - No user-uploaded content.
 *   - No live social scraping / embeds.
 *   - No DB persistence, no admin UI.
 *
 * Photo entry shape (from `content/social.js`):
 *   { id, src, webp?, alt, caption?, credit?, aspect? }
 */
import React, { useCallback, useEffect, useState } from "react";
import { Camera, X, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { SOCIAL_GALLERY } from "@/content/social";

/* ---------------------------------------------------------------------------
   GalleryPhoto — single masonry tile.
   Uses <picture> for webp/jpg fallback. Gracefully degrades to a
   dashed placeholder if the asset fails to load.
   --------------------------------------------------------------------------- */
function GalleryPhoto({ photo, onClick, testId }) {
  const [failed, setFailed] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open photo: ${photo.alt}`}
      className="block w-full text-left rounded-[20px] overflow-hidden border border-pp-line bg-pp-v3-bg transition-transform duration-200 hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-pp-orange focus-visible:ring-offset-2"
      style={{ boxShadow: "var(--pp-shadow)" }}
      data-testid={testId}
    >
      {failed ? (
        <div
          className="aspect-[4/5] flex items-center justify-center text-center px-6 bg-pp-v3-surface"
          data-testid={`${testId}-failed`}
        >
          <span className="pp-sm" style={{ maxWidth: "24em" }}>
            {photo.alt}
          </span>
        </div>
      ) : (
        <picture>
          {photo.webp && <source srcSet={photo.webp} type="image/webp" />}
          <img
            src={photo.src}
            alt={photo.alt}
            className="block w-full h-auto object-cover"
            loading="lazy"
            onError={() => setFailed(true)}
          />
        </picture>
      )}
      {(photo.caption || photo.credit) && (
        <div className="px-4 py-3">
          {photo.caption && (
            <p className="text-[14px] text-pp-slate leading-snug">
              {photo.caption}
            </p>
          )}
          {photo.credit && (
            <p className="text-[12px] text-pp-text-soft mt-1">
              {photo.credit}
            </p>
          )}
        </div>
      )}
    </button>
  );
}

/* ---------------------------------------------------------------------------
   PhotoLightbox — Radix `Dialog` overridden to fill a wider viewport.
   Adds keyboard arrow-left / arrow-right while open. ESC + overlay-
   click are handled by Dialog defaults.
   --------------------------------------------------------------------------- */
function PhotoLightbox({ photos, index, open, onClose, onIndexChange }) {
  const canNavigate = photos.length > 1;
  const goPrev = useCallback(() => {
    if (!canNavigate) return;
    onIndexChange((index - 1 + photos.length) % photos.length);
  }, [canNavigate, index, photos.length, onIndexChange]);
  const goNext = useCallback(() => {
    if (!canNavigate) return;
    onIndexChange((index + 1) % photos.length);
  }, [canNavigate, index, photos.length, onIndexChange]);

  // Keyboard navigation — only attached while the lightbox is open.
  useEffect(() => {
    if (!open || !canNavigate) return undefined;
    const handler = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, canNavigate, goPrev, goNext]);

  if (!photos.length) return null;
  const photo = photos[index] || photos[0];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-[min(960px,92vw)] max-h-[90vh] p-0 gap-0 border-0 bg-transparent shadow-none overflow-hidden"
        data-testid="pluckin-lightbox"
      >
        {/* Visually-hidden a11y heading + description for screen readers. */}
        <DialogTitle className="sr-only">
          {photo.alt}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {photo.caption || photo.alt}
        </DialogDescription>

        <div className="relative flex flex-col items-center justify-center bg-black rounded-[20px] overflow-hidden">
          {/* Close button — Radix Dialog renders its own corner X via
              child DialogClose, but we want a branded one. */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close photo"
            className="absolute top-3 right-3 z-10 inline-flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white p-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pp-orange"
            data-testid="pluckin-lightbox-close"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>

          {canNavigate && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous photo"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white p-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pp-orange"
                data-testid="pluckin-lightbox-prev"
              >
                <ChevronLeft className="h-6 w-6" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next photo"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white p-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pp-orange"
                data-testid="pluckin-lightbox-next"
              >
                <ChevronRight className="h-6 w-6" aria-hidden="true" />
              </button>
            </>
          )}

          <picture>
            {photo.webp && <source srcSet={photo.webp} type="image/webp" />}
            <img
              src={photo.src}
              alt={photo.alt}
              className="block max-h-[80vh] w-auto h-auto object-contain"
              data-testid="pluckin-lightbox-image"
            />
          </picture>

          {(photo.caption || photo.credit || canNavigate) && (
            <div className="w-full bg-black/80 text-white px-5 py-3 flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                {photo.caption && (
                  <p className="text-[14px] leading-snug truncate">
                    {photo.caption}
                  </p>
                )}
                {photo.credit && (
                  <p className="text-[12px] text-white/70 mt-0.5 truncate">
                    {photo.credit}
                  </p>
                )}
              </div>
              {canNavigate && (
                <span
                  className="text-[12px] font-bold uppercase tracking-[0.08em] text-white/70 shrink-0"
                  data-testid="pluckin-lightbox-counter"
                >
                  {index + 1} of {photos.length}
                </span>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------------------------------
   EmptyState — branded "Pluckin' Around — photos coming soon." card.
   Mirrors the v3 empty/error-state pattern from Pricing's `ErrorBanner`
   for visual consistency. No CTA — this is a soft "we're working on it",
   not an actionable failure.
   --------------------------------------------------------------------------- */
function EmptyState() {
  return (
    <div
      className="rounded-[20px] border border-pp-line bg-pp-v3-bg p-7 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-5 max-w-2xl mx-auto"
      style={{ boxShadow: "var(--pp-shadow)" }}
      data-testid="pluckin-empty-state"
    >
      <span
        className="inline-flex items-center justify-center rounded-[12px]"
        style={{
          width: 52,
          height: 52,
          background: "rgba(242,151,43,0.15)",
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        <Camera
          className="h-6 w-6"
          style={{ color: "var(--pp-orange)" }}
          strokeWidth={2}
        />
      </span>
      <div className="flex-1">
        <p
          className="text-[17px] sm:text-[18px] font-bold text-pp-slate leading-snug"
          data-testid="pluckin-empty-state-copy"
        >
          {SOCIAL_GALLERY.empty_state_copy}
        </p>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   PluckinAroundGallery — main export.
   --------------------------------------------------------------------------- */
export default function PluckinAroundGallery() {
  const photos = SOCIAL_GALLERY.photos || [];
  const hasPhotos = photos.length > 0;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openLightbox = (i) => {
    setActiveIndex(i);
    setLightboxOpen(true);
  };

  return (
    <section
      style={{ background: "var(--pp-v3-surface)" }}
      data-testid="pluckin-gallery-section"
    >
      <div className="pp-wrap py-16 md:py-24">
        <div className="max-w-2xl">
          <span className="pp-kick" data-testid="pluckin-gallery-kick">
            {SOCIAL_GALLERY.section_kick}
          </span>
          <p
            className="pp-customer-p mt-4"
            data-testid="pluckin-gallery-subhead"
          >
            {SOCIAL_GALLERY.section_subhead}
          </p>
        </div>

        <div className="mt-12" data-testid="pluckin-gallery-region">
          {hasPhotos ? (
            <div
              className="columns-1 sm:columns-2 lg:columns-3 gap-4"
              data-testid="pluckin-gallery-grid"
            >
              {photos.map((photo, i) => (
                <div
                  key={photo.id}
                  className="break-inside-avoid mb-4"
                >
                  <GalleryPhoto
                    photo={photo}
                    onClick={() => openLightbox(i)}
                    testId={`pluckin-photo-${photo.id}`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState />
          )}
        </div>
      </div>

      {hasPhotos && (
        <PhotoLightbox
          photos={photos}
          index={activeIndex}
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onIndexChange={setActiveIndex}
        />
      )}
    </section>
  );
}
